import { useRef } from 'react';
import {
  FORECAST_MIN_DAY,
  FORECAST_MAX_DAY,
  FORECAST_DAYS,
  FORECAST_RANGE,
} from '../../../constants/accessRightsConfig';
import './ForecastRuler.css';

// Props :
//   availableMinJ — borne gauche que le handle min ne peut pas dépasser
//   availableMaxJ — borne droite que le handle max ne peut pas dépasser
const ForecastRuler = ({
  minVal, maxVal,
  availableMinJ, availableMaxJ,
  onChange, disabled,
}) => {
  const trackRef    = useRef(null);
  const dragging    = useRef(null);
  const dragStartX  = useRef(null); // ← NOUVEAU : position X au pointerdown, pour résoudre les cas ambigus

  const effectiveMin = minVal ?? availableMinJ ?? FORECAST_MIN_DAY;
  const effectiveMax = maxVal ?? availableMaxJ ?? FORECAST_MAX_DAY;

  // Bornes réelles autorisées
  const clampMin = availableMinJ ?? FORECAST_MIN_DAY;
  const clampMax = availableMaxJ ?? FORECAST_MAX_DAY;

  const pct      = (val) => ((val - FORECAST_MIN_DAY) / FORECAST_RANGE) * 100;
  const dayLabel = (d)   => d === 0 ? 'J0' : d > 0 ? `J+${d}` : `J${d}`;

  const valFromClientX = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(FORECAST_MIN_DAY + ratio * FORECAST_RANGE);
  };

  const onPointerDown = (e, which) => {
    if (disabled) return;
    e.preventDefault(); e.stopPropagation();

    // ── NOUVEAU : cas ambigu — les deux poignées sont au même endroit ──
    // Impossible de savoir de manière fiable, via le DOM, laquelle des deux
    // l'utilisateur veut réellement bouger. On diffère la décision jusqu'au
    // premier mouvement significatif du pointeur (voir onPointerMove).
    if (effectiveMin === effectiveMax) {
      dragging.current = 'auto';
      dragStartX.current = e.clientX;
    } else {
      dragging.current = which;
    }

    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onPointerMove = (e) => {
    if (!dragging.current || disabled) return;
    e.preventDefault();

    // ── NOUVEAU : résolution différée de la poignée ambiguë ──
    // Le sens du tout premier mouvement décide : vers la gauche → min,
    // vers la droite → max. Une fois décidé, on garde ce choix pour
    // tout le reste du glissement (jusqu'au pointerup).
    if (dragging.current === 'auto') {
      const delta = e.clientX - dragStartX.current;
      if (Math.abs(delta) < 3) return; // ignore les micro-mouvements/tremblements
      dragging.current = delta < 0 ? 'min' : 'max';
    }

    const v = valFromClientX(e.clientX);
    if (dragging.current === 'min') {
      // Clampé entre clampMin et 0
      const c = Math.max(clampMin, Math.min(0, v));
      if (c !== effectiveMin) onChange('intervalMinJ', c);
    } else {
      // Clampé entre 0 et clampMax
      const c = Math.min(clampMax, Math.max(0, v));
      if (c !== effectiveMax) onChange('intervalMaxJ', c);
    }
  };

  const onPointerUp = () => { dragging.current = null; dragStartX.current = null; };

  const labelClass = (d) => {
    // Hors des bornes disponibles → grisé
    if (d < clampMin || d > clampMax) return 'arm-ruler-day-label arm-ruler-day-label--unavailable';
    if (d === 0)                       return 'arm-ruler-day-label arm-ruler-day-label--zero';
    if (d >= effectiveMin && d < 0)    return 'arm-ruler-day-label arm-ruler-day-label--active-past';
    if (d > 0 && d <= effectiveMax)    return 'arm-ruler-day-label arm-ruler-day-label--active-future';
    return 'arm-ruler-day-label';
  };

  const hasPast   = effectiveMin < 0;
  const hasFuture = effectiveMax > 0;

  return (
    <div className={`arm-forecast-ruler${disabled ? ' arm-forecast-ruler--disabled' : ''}`}>
      {/* Indication de la plage disponible */}
      {(clampMin !== FORECAST_MIN_DAY || clampMax !== FORECAST_MAX_DAY) && (
        <div className="arm-ruler-available-hint">
          Plage disponible : {dayLabel(clampMin)} → {dayLabel(clampMax)}
        </div>
      )}

      <div className="arm-ruler-track-wrap" ref={trackRef}>
        <div className="arm-ruler-track">

          {/* Zone non disponible à gauche */}
          {clampMin > FORECAST_MIN_DAY && (
            <div className="arm-ruler-seg-unavailable"
              style={{ left: `${pct(FORECAST_MIN_DAY)}%`, width: `${pct(clampMin) - pct(FORECAST_MIN_DAY)}%` }} />
          )}
          {/* Zone non disponible à droite */}
          {clampMax < FORECAST_MAX_DAY && (
            <div className="arm-ruler-seg-unavailable"
              style={{ left: `${pct(clampMax)}%`, width: `${pct(FORECAST_MAX_DAY) - pct(clampMax)}%` }} />
          )}

          {hasPast && (
            <div className="arm-ruler-seg-past"
              style={{ left: `${pct(effectiveMin)}%`, width: `${pct(0) - pct(effectiveMin)}%` }} />
          )}
          {hasFuture && (
            <div className="arm-ruler-seg-future"
              style={{ left: `${pct(0)}%`, width: `${pct(effectiveMax) - pct(0)}%` }} />
          )}
          {FORECAST_DAYS.map((d) => (
            <div key={d}
              className={`arm-ruler-tick${d === 0 ? ' arm-ruler-tick--zero' : ''}`}
              style={{ left: `${pct(d)}%` }} />
          ))}

          <div className="arm-ruler-handle" style={{ left: `${pct(effectiveMin)}%` }}
            onPointerDown={(e) => onPointerDown(e, 'min')} onPointerMove={onPointerMove}
            onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            role="slider" aria-label="Début plage"
            aria-valuemin={clampMin} aria-valuemax={0} aria-valuenow={effectiveMin}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'ArrowLeft')  onChange('intervalMinJ', Math.max(clampMin, effectiveMin - 1));
              if (e.key === 'ArrowRight') onChange('intervalMinJ', Math.min(0, effectiveMin + 1));
            }}
          >
            <div className="arm-ruler-bubble">{dayLabel(effectiveMin)}</div>
            <div className="arm-ruler-handle-dot" />
          </div>

          <div className="arm-ruler-handle arm-ruler-handle--future" style={{ left: `${pct(effectiveMax)}%` }}
            onPointerDown={(e) => onPointerDown(e, 'max')} onPointerMove={onPointerMove}
            onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
            role="slider" aria-label="Fin plage"
            aria-valuemin={0} aria-valuemax={clampMax} aria-valuenow={effectiveMax}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'ArrowLeft')  onChange('intervalMaxJ', Math.max(0, effectiveMax - 1));
              if (e.key === 'ArrowRight') onChange('intervalMaxJ', Math.min(clampMax, effectiveMax + 1));
            }}
          >
            <div className="arm-ruler-bubble">{dayLabel(effectiveMax)}</div>
            <div className="arm-ruler-handle-dot" />
          </div>
        </div>
      </div>

      <div className="arm-ruler-labels">
        {FORECAST_DAYS.map((d) => (
          <span key={d} className={labelClass(d)}>{dayLabel(d)}</span>
        ))}
      </div>
    </div>
  );
};

export default ForecastRuler;